import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Eyebrow, LineReveal } from "../components/motion";
import Marquee from "../components/Marquee";
import BookingCta from "../components/BookingCta";
import PerfectStopover from "../components/PerfectStopover";
import { IMAGES, DOMES } from "../config/site";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] min-h-[640px] overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <img src={IMAGES.hero} alt="Geodesic domes at dusk on the lavender farm" className="h-full w-full object-cover object-[0%_50%] lg:object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/35" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-6xl mx-auto px-6 md:px-8 flex flex-col justify-end pt-24 pb-20">
        <Eyebrow className="mb-6" >Danseys Pass · North Otago · Aotearoa New Zealand</Eyebrow>
        <LineReveal
          lines={["Stay somewhere quieter,", "wilder, & a little", "more unforgettable."]}
          className="display-1 text-cream"
          lineClass="text-cream"
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 max-w-xl text-sage text-sm md:text-base leading-relaxed"
        >
          Two luxury geodesic domes on a high-country lavender farm — private hot tubs,
          an outdoor riverside sauna, and big southern skies.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <Link to="/domes" className="btn-primary" data-testid="hero-explore-domes-button">Explore the domes</Link>
          <Link to="/book" className="btn-ghost" data-testid="hero-check-availability-button">Check availability</Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15, duration: 1 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[0.62rem] uppercase tracking-widest2 text-sage/80"
        >
          <span>Two luxury domes</span>
          <span>Maerewhenua River</span>
          <span>Lavender fields in season</span>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 right-8 z-10 text-sage hidden md:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
      >
        <ArrowDown size={18} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function Intro() {
  return (
    <section className="py-24 md:py-36 px-6 md:px-16" data-testid="home-intro-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow className="mb-6">A high-country escape</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-2 text-cream">
              Remote enough to feel like a true escape.{" "}
              <span className="serif-italic text-lavglow">Perfectly placed</span> for the journey.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-sage mt-8 leading-relaxed max-w-xl text-sm md:text-base">
              Danseys Pass Lavender Farm sits beside the Maerewhenua River, between Queenstown,
              Wānaka, Aoraki / Mount Cook and Christchurch. Wake to mountain views, soak in your
              private hot tub, wander by the river, and experience a quieter side of the South Island —
              whether you're stopping for a night on a road trip or settling in for a few days.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="text-fern mt-5 leading-relaxed max-w-xl text-sm md:text-base">
              Switch off, breathe deeply, and enjoy the landscape around you.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1} y={40}>
            <div className="img-frame aspect-[4/5]">
              <img src={IMAGES.lavender} alt="Lavender rows at golden hour" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function DomesPreview() {
  const domes = [
    { to: "/domes#piwakawaka", img: IMAGES.piwakawakaCover, num: DOMES[0].number, name: "Piwakawaka", note: "Mountain View Dome · Private hot tub · Sleeps 4", testid: "home-dome-piwakawaka-card" },
    { to: "/domes#karearea", img: IMAGES.kareareaCover, num: DOMES[1].number, name: "Kārearea", note: "Luxury Dome · Deck with BBQ · Sleeps 4", testid: "home-dome-karearea-card" },
  ];
  return (
    <section className="py-24 md:py-32 px-6 md:px-16" data-testid="home-domes-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <Reveal><Eyebrow className="mb-5">The accommodation</Eyebrow></Reveal>
            <Reveal delay={0.08}><h2 className="display-2 text-cream">Two domes. One <span className="serif-italic text-lavglow">wild valley.</span></h2></Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/domes" className="link-underline font-mono text-[0.68rem] uppercase tracking-[0.18em] text-sage hover:text-cream" data-testid="home-view-all-domes-link">
              View the domes
            </Link>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {domes.map((d, i) => (
            <Reveal key={d.name} delay={i * 0.12} testid={d.testid}>
              <Link to={d.to} className="group block">
                <div className="img-frame aspect-[4/3]">
                  <img src={d.img} alt={`Geodesic ${d.name} dome`} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-7">
                    <p className="font-mono text-[0.62rem] uppercase tracking-widest2 text-lavglow mb-2">{d.num}</p>
                    <p className="font-display text-3xl md:text-4xl text-cream">{d.name}</p>
                    <p className="text-sage text-xs mt-2 tracking-wide">{d.note}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Wellness() {
  return (
    <section className="py-24 md:py-32 px-6 md:px-16 bg-moss" data-testid="home-wellness-section">
      <div className="max-w-6xl mx-auto">
        <Reveal><Eyebrow className="mb-5 text-center block">The ritual</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="display-2 text-cream text-center mb-16">Soak. Sweat. <span className="serif-italic text-lavglow">Breathe.</span></h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { img: IMAGES.hottub, t: "Private hot tubs", c: "Every dome has its own private hot tub — sink into warm water under the open sky and watch the light move across the hills.", testid: "wellness-hottub-card" },
            { img: IMAGES.sauna, t: "Riverside sauna", c: "Our outdoor sauna sits beside the Maerewhenua River. Heat up, step out, and let the high-country air take over.", testid: "wellness-sauna-card" },
          ].map((f, i) => (
            <Reveal key={f.t} delay={i * 0.12} testid={f.testid}>
              <div className="group">
                <div className="img-frame aspect-[3/2]">
                  <img src={f.img} alt={f.t} className="h-full w-full object-cover" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-cream mt-6">{f.t}</h3>
                <p className="text-sage text-sm leading-relaxed mt-3 max-w-md">{f.c}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Landscape() {
  const shots = [
    { img: IMAGES.frost, alt: "Winter frost over the paddocks" },
    { video: true, alt: "Harvesting the lavender rows in summer" },
    { img: IMAGES.dogLavender, alt: "Farm dog peeking through the lavender" },
  ];
  return (
    <section className="py-24 md:py-32 px-6 md:px-16" data-testid="home-landscape-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow className="mb-6">The land</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="display-2 text-cream">A working farm, <span className="serif-italic text-lavglow">a quiet valley.</span></h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="text-sage mt-8 leading-relaxed text-sm md:text-base">
                Lavender rows in summer, frost on the paddocks in winter, stock grazing by the
                river all year round. Days here are unhurried — read a book, explore the lavender
                fields in season, enjoy the sauna, or head out for a drive through Danseys Pass.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="text-fern mt-5 leading-relaxed text-sm md:text-base">
                In the evening, settle in as the valley grows still and the stars come out overhead.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid grid-cols-3 gap-3 md:gap-4">
            {shots.map((s, i) => (
              <Reveal key={s.alt} delay={i * 0.1} y={40}>
                <div className="img-frame aspect-[3/4]">
                  {s.video ? (
                    <video
                      src="/videos/harvest.mp4"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      poster="/images/harvest-poster.jpg"
                      className="h-full w-full object-cover"
                      data-testid="farm-video"
                    />
                  ) : (
                    <img src={s.img} alt={s.alt} className="h-full w-full object-cover" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Intro />
      <DomesPreview />
      <Marquee
        items={["Private hot tubs", "Riverside sauna", "Lavender fields in season", "Maerewhenua River", "Danseys Pass", "Aotearoa New Zealand"]}
      />
      <Wellness />
      <Landscape />
      <PerfectStopover />
      <BookingCta />
    </>
  );
}
